'use client';

import { ProfilePage } from '~/components/homes/profile/components/Page';
import { ReadonlyURLSearchParams } from 'next/navigation';
import { useModalWithUrl } from '~/hooks/useModalWithUrl';
import dynamic from 'next/dynamic';
import { NestedHome } from '@habity/types';
import HomeNotFound from '../../utility/404/page-home';
import { useUser } from '~/hooks/useUser';
import { getQueryFromFilters, isHomeOwner } from '@habity/services';
import { hasVirtualTour } from '~/lib/homes/profile/virtualTour';
import { getTableKey } from '~/lib/providers/supabase/swr';
import { getAPI } from '~/lib/api';
import useSWRInfinite from 'swr/infinite';
import { getRelatedHomesFilters } from '@habity/agencies';
import { useAgency } from '~/hooks/useAgency';
import { useObserver } from '~/hooks/useObserver';
import { ViewingModal } from '~/components/ui/modals/ViewingModal';
import { isAdmin, isBrokerOrAdmin } from '~/lib/roles';
import { useUpdateSlug } from '~/hooks/useUpdateSlug';
import { SEARCH_CONFIG } from '@habity/constants';
import { useState } from 'react';
// import { useTracking } from '~/hooks/useTracking';
import { useVitecAnalytics } from '~/hooks/providers/useVitecAnalytics';
import type { VitecAnalyticsConfig } from '@habity/types';

const ProviderDataModal = dynamic(
  () =>
    import('~/components/ui/modals/ProviderDataModal').then(
      (mod) => mod.ProviderDataModal
    ),
  {
    ssr: false,
    loading: () => null,
  }
);

const VirtualTourModal = dynamic(
  () =>
    import('~/components/ui/modals/VirtualTourModal').then(
      (mod) => mod.VirtualTourModal
    ),
  {
    ssr: false,
    loading: () => null,
  }
);

const HomeGalleryModal = dynamic(
  () =>
    import('~/components/ui/modals/HomeGalleryModal').then(
      (mod) => mod.HomeGalleryModal
    ),
  {
    ssr: false,
    loading: () => null,
  }
);

export const HomeProfile = ({ home }: { home: NestedHome }) => {
  const {
    galleryOpen,
    floorPlanOpen,
    virtualTourOpen,
    viewingsOpen,
    handleOpenWithUrlUpdate,
  } = useModalWithUrl(home);

  const { user } = useUser();
  const [initialViewingId, setInitialViewingId] = useState<number>();

  const { agency, getProviderSettings } = useAgency();
  const { identifier } = agency || {};
  // const { track } = useTracking();

  // Update the URL with the complete slug if it's missing parts
  useUpdateSlug(home);

  // Vitec Analytics – track estate page views
  const vitecAnalyticsConfig = getProviderSettings(
    'vitec_analytics'
  ) as VitecAnalyticsConfig | null;
  useVitecAnalytics({
    apiKey: vitecAnalyticsConfig?.apiKey,
    estateId: home?.provider_id ?? undefined,
    customerId: home?.provider_customer_id,
  });

  // Track home profile view for analytics
  // useEffect(() => {
  //   if (home?.id) {
  //     const municipality = home.address?.municipality;
  //     track(EVENTS.VIEWED_HOME_PROFILE, {
  //       home_id: home.id,
  //       home_provider_id: home.provider_id,
  //       home_status: home.status?.name,
  //       home_category: home.address?.category,
  //       home_area: home.address?.area?.name,
  //       home_municipality:
  //         typeof municipality === 'string'
  //           ? municipality
  //           : (municipality as { name?: string } | null)?.name,
  //     });
  //   }
  // }, [home?.id]);

  const relatedHomesFilters =
    home && identifier ? getRelatedHomesFilters(home, identifier) : null;
  const relatedHomesParams = getQueryFromFilters(relatedHomesFilters);

  const relatedHomes = useSWRInfinite<TODO[]>(
    (page, previousPageData) =>
      relatedHomesParams && typeof window !== 'undefined'
        ? getTableKey({
            page,
            previousPageData,
            tab: 'related-homes',
            searchParams: relatedHomesParams as ReadonlyURLSearchParams,
            limit: SEARCH_CONFIG.PER_PAGE.RELATED_HOMES,
            useLimitAsTotalCount: true,
          })
        : null,
    getAPI,
    {
      revalidateOnFocus: false,
      revalidateFirstPage: false,
      revalidateOnMount: true,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      suspense: false,
      keepPreviousData: true,
      dedupingInterval: 60000, // Dedupe requests for 1 minute
      focusThrottleInterval: 60000, // Throttle focus revalidation
      errorRetryCount: 3, // Limit error retries
      loadingTimeout: 5000, // Timeout after 5 seconds
    }
  );
  const {
    isLoading: relatedHomesLoading,
    isValidating: relatedHomesValidating,
  } = relatedHomes;

  const relatedHomesData = relatedHomes?.data?.flatMap((h) => h) || [];
  const relatedHomesCount = relatedHomesData.length;
  const MAX_RELATED_HOMES = 48;
  const totalRelatedHomes = Math.min(
    Number(relatedHomesData?.[0]?.count || 0),
    MAX_RELATED_HOMES
  );

  const { hasMore, loadMore } = useObserver({
    swr: relatedHomes,
    isCompleted:
      relatedHomesCount > 0 && relatedHomesCount >= totalRelatedHomes,
  });

  const isOnlyVisibleForOwnerAndAdmins =
    home.is_hidden && !isHomeOwner(home, user) && !isAdmin(user);

  if (!home || isOnlyVisibleForOwnerAndAdmins)
    return (
      <HomeNotFound
        home={home}
        relatedHomes={relatedHomesData as NestedHome[]}
        relatedHomesLoading={relatedHomesLoading || relatedHomesValidating}
        hasMore={hasMore}
        loadMore={loadMore}
      />
    );

  return (
    <>
      <ProfilePage
        home={home}
        relatedHomes={relatedHomesData as NestedHome[]}
        relatedHomesLoading={relatedHomesLoading || relatedHomesValidating}
        hasMore={hasMore}
        loadMore={loadMore}
        onOpenViewingModal={(viewingId) => {
          setInitialViewingId(viewingId);
          handleOpenWithUrlUpdate({ modalType: 'viewings', open: true });
        }}
      />
      {isBrokerOrAdmin(user) && <ProviderDataModal home={home} />}
      <HomeGalleryModal
        home={home}
        open={galleryOpen || floorPlanOpen}
        handleOpen={handleOpenWithUrlUpdate}
        imageType={floorPlanOpen ? 'floorPlan' : 'all'}
      />
      {hasVirtualTour(home) && (
        <VirtualTourModal
          home={home}
          open={virtualTourOpen}
          handleOpen={handleOpenWithUrlUpdate}
        />
      )}
      <ViewingModal
        home={home}
        isOpen={viewingsOpen}
        onClose={() => {
          handleOpenWithUrlUpdate({ modalType: 'viewings', open: false });
          setInitialViewingId(undefined);
        }}
        initialViewingId={initialViewingId}
      />
    </>
  );
};
